import { getDefaultVisibleScanListName } from "../radio/radio-layout";

export interface ChannelModel {
  index: number;
  active: boolean;
  name: string;
  rxFrequency: number;
  band: number;
  scanlistMask: number;
  stepSetting: number;
  bandwidth: "wide" | "narrow";
}

export type ScanTargetMode = "lists" | "all";
export type ScanResumeMode = "stop" | "dwell";
export type ArrowOrientation = "up_down" | "left_right";
export type NavigationMode = "channel" | "id";
export type ForceBandwidthMode = "dont_force" | "wide" | "narrow" | "narrower";
export type PowerOnDisplayMode = "message" | "sound" | "voltage" | "logo" | "none";
export type RogerMode = "off" | "roger" | "mdc";
export type CrossBandMode = "off" | "chan_a" | "chan_b";
export type DualWatchMode = "off" | "chan_a" | "chan_b";

export interface RadioActiveOptions {
  beep_control: boolean;
  bpm_control: boolean;
  display_strength: boolean;
  battery_type: number;
  scan: {
    active_list_mask: number;
    list_names: string[];
    resume_mode: ScanResumeMode;
    dwell_time_seconds: number;
  };
  telemetry: {
    peak_time: number;
  };
  receiver: {
    squelch_level: number;
    rf_gain: number;
    navigation_mode: NavigationMode;
  };
  display: {
    power_on_display_mode: PowerOnDisplayMode;
    backlight_time: number;
  };
  boot: {
    line1: string;
    line2: string;
  };
  keys: {
    flashlight_enabled: boolean;
    arrow_orientation: ArrowOrientation;
  };
  meter_calibration: {
    s0_level: number;
    s9_level: number;
  };
  developer: {
    pulse_threshold_dbm: number;
    full_scale_dbm: number;
    frequency_offset_hz: number;
    force_bandwidth: ForceBandwidthMode;
    battery_calibration: number;
    present: {
      squelch_level: boolean;
      pulse_threshold_dbm: boolean;
      full_scale_dbm: boolean;
      frequency_offset_hz: boolean;
      force_bandwidth: boolean;
      battery_calibration: boolean;
    };
  };
}

export interface RadioLegacyValues {
  tx_timeout_timer: number;
  mic_sensitivity: number;
  roger_mode: RogerMode;
  repeater_tail_tone_elimination: number;
  tx_vfo_index: number;
  auto_keypad_lock: boolean;
  cross_band_rx_tx: CrossBandMode;
  dual_watch: DualWatchMode;
  vfo_open: boolean;
  raw: Record<string, unknown>;
}

export interface RadioStateModel {
  activeOptions: RadioActiveOptions;
  legacyValues: RadioLegacyValues;
}

export function createDefaultRadioState(): RadioStateModel {
  return {
    activeOptions: {
      beep_control: false,
      bpm_control: true,
      display_strength: false,
      battery_type: 0,
      scan: {
        active_list_mask: 0,
        list_names: Array.from({ length: 16 }, (_, i) => getDefaultVisibleScanListName(i)),
        resume_mode: "dwell",
        dwell_time_seconds: 3
      },
      telemetry: {
        peak_time: 22
      },
      receiver: {
        squelch_level: 1,
        rf_gain: 12,
        navigation_mode: "channel"
      },
      display: {
        power_on_display_mode: "voltage",
        backlight_time: 3
      },
      boot: {
        line1: "",
        line2: ""
      },
      keys: {
        flashlight_enabled: true,
        arrow_orientation: "up_down"
      },
      meter_calibration: {
        s0_level: 130,
        s9_level: 76
      },
      developer: {
        pulse_threshold_dbm: -95,
        full_scale_dbm: -31,
        frequency_offset_hz: 0,
        force_bandwidth: "dont_force",
        battery_calibration: 2300,
        present: {
          squelch_level: false,
          pulse_threshold_dbm: false,
          full_scale_dbm: false,
          frequency_offset_hz: false,
          force_bandwidth: false,
          battery_calibration: false
        }
      }
    },
    legacyValues: {
      tx_timeout_timer: 1,
      mic_sensitivity: 4,
      roger_mode: "off",
      repeater_tail_tone_elimination: 0,
      tx_vfo_index: 0,
      auto_keypad_lock: false,
      cross_band_rx_tx: "off",
      dual_watch: "off",
      vfo_open: true,
      raw: {}
    }
  };
}
